import { useState, useEffect, useRef, useCallback, useMemo } from 'react';

interface UseLazyImageOptions {
  rootMargin?: string;
  threshold?: number;
  enabled?: boolean;
}

interface UseLazyImageResult {
  ref: React.RefObject<HTMLElement | null>;
  isInView: boolean;
  isLoaded: boolean;
  isError: boolean;
  onLoad: () => void;
  onError: () => void;
}

export function useLazyImage(
  src: string | null,
  options: UseLazyImageOptions = {}
): UseLazyImageResult {
  const { rootMargin = '200px', threshold = 0, enabled = true } = options;
  const ref = useRef<HTMLElement | null>(null);
  const hasIntersectionObserver = 'IntersectionObserver' in window;
  const shouldSkipObserver = !enabled || !src || !hasIntersectionObserver;
  const [observedInView, setObservedInView] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isError, setIsError] = useState(false);

  const isInView = useMemo(() => {
    return shouldSkipObserver || observedInView;
  }, [shouldSkipObserver, observedInView]);

  useEffect(() => {
    if (shouldSkipObserver) {
      return;
    }

    const element = ref.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry?.isIntersecting) {
          setObservedInView(true);
          observer.disconnect();
        }
      },
      { rootMargin, threshold }
    );

    observer.observe(element);

    return () => observer.disconnect();
  }, [src, rootMargin, threshold, shouldSkipObserver]);

  const onLoad = useCallback(() => {
    setIsLoaded(true);
  }, []);

  const onError = useCallback(() => {
    setIsError(true);
  }, []);

  return { ref, isInView, isLoaded, isError, onLoad, onError };
}
